import React, { useState } from "react";
import { Ruler } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { RING_SIZES } from "@/lib/ringSizes";

const LINKS = [["Order a free ring sizer", "/pages/customer-care#ring-sizing"], ["Printable ring sizer", "/ring-sizer"], ["Full Ring Size Guide", "/education/ring-size-guide"]];

export function RingSizeHelp({ sizes, value, onPick, testId = "ring-size-help" }: { sizes: string[]; value?: string; onPick: (s: string) => void; testId?: string }) {
  const [open, setOpen] = useState(false);
  const rows = RING_SIZES.filter((r) => sizes.includes(String(r.us)));
  const pick = (s: string) => { onPick(s); setOpen(false); };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-xs underline underline-offset-4 hover:text-[var(--c-primary)] transition-colors flex items-center gap-1.5" data-testid={`${testId}-button`}>
        <Ruler size={13} strokeWidth={1.4} />Not sure of your size?
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogTitle className="font-display text-3xl">Find your ring size</DialogTitle>
          <p className="text-sm text-neutral-600 -mt-1">Our rings are listed in US sizes. Find your size in your country's column, then tap the row to select it.</p>
          <table className="w-full text-sm border border-neutral-200" data-testid={`${testId}-table`}>
            <thead className="bg-neutral-50 text-left"><tr>{["US", "UK / AU", "EU", "Diameter"].map((h) => <th key={h} className="px-3 py-2 eyebrow text-[10px] font-normal text-neutral-500">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map((r) => { const on = value === String(r.us); return (
                <tr key={r.us} onClick={() => pick(String(r.us))} className={`cursor-pointer transition-colors ${on ? "bg-neutral-900 text-white" : "hover:bg-[var(--c-muted)]"}`} data-testid={`${testId}-row-${r.us}`}>
                  <td className="px-3 py-2 font-medium">{r.us}</td><td className="px-3 py-2">{r.uk}</td><td className="px-3 py-2">{r.eu}</td><td className="px-3 py-2">{r.d} mm</td>
                </tr>
              ); })}
            </tbody>
          </table>
          <ul className="text-xs text-neutral-600 space-y-1.5 list-disc pl-4">
            <li>Measure at the end of the day, when your fingers are warm and at their largest.</li>
            <li>A good fit slides over the knuckle with a gentle push and needs a gentle pull to remove.</li>
            <li>Choosing a wide band (6 mm or more)? Consider going up half a size.</li>
            <li>Not quite right? We resize free of charge within 90 days.</li>
          </ul>
          <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
            {LINKS.map(([l, u]) => <a key={u} href={u} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4" data-testid={`${testId}-link-${u.split("/").pop()?.split("#")[0]}`}>{l}</a>)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
